import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { TrustBand } from "@/components/site/TrustBand";
import { EditorialHero } from "@/components/site/wow/EditorialHero";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { aeoGeo } from "@/lib/seo/schema";
import {
  getAnswersByLanguage,
  getAnswerSlug,
  toFaqJsonLd,
} from "@/lib/aeo/answers";

const QUESTIONS = getAnswersByLanguage("sv");

export const Route = createFileRoute("/fragor")({
  head: () => ({
    meta: [
      { title: "Vanliga frågor om film, abonnemang och pris | Fenny" },
      {
        name: "description",
        content:
          "Korta, raka svar på de vanligaste frågorna om filmproduktion, film på abonnemang och vad film kostar. Faktagranskade svar från Fenny.",
      },
      { property: "og:title", content: "Frågor & svar om film | Fenny" },
      {
        property: "og:description",
        content:
          "Faktagranskade svar på vad film kostar, hur film på abonnemang fungerar och skillnaden mellan Pulse och Prime.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/fragor" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/fragor" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/fragor", name: "Vanliga frågor om film, abonnemang och pris | Fenny", description: "Korta, raka svar på de vanligaste frågorna om filmproduktion, film på abonnemang och vad film kostar. Faktagranskade svar från Fenny."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(toFaqJsonLd(QUESTIONS.map((q) => q.id))),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: QUESTIONS.map((q, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: q.question,
            url: `/fragor/${getAnswerSlug(q)}`,
          })),
        }),
      },
    ],
  }),
  component: FragorHubPage,
});

function FragorHubPage() {
  return (
    <div>
      <EditorialHero
        eyebrow="Frågor & svar"
        title="Raka svar om film."
        highlight={[2, 3]}
        tone="mint"
        lede="De vanligaste frågorna vi får om filmproduktion, film på abonnemang och vad film faktiskt kostar — besvarade kort och faktagranskat."
      />

      <Section className="bg-surface/40">
        <EyebrowHeading
          eyebrow="Alla frågor"
          title="Välj en fråga"
          align="center"
        />
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {QUESTIONS.map((q) => (
            <Link
              key={q.id}
              to="/fragor/$slug"
              params={{ slug: getAnswerSlug(q) }}
              className="group flex flex-col rounded-3xl border border-white/10 bg-surface p-7 transition-colors hover:border-mint/30"
            >
              <h2 className="text-xl font-semibold leading-tight">
                {q.question}
              </h2>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground/80">
                {q.shortAnswer}
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm text-foreground/80 group-hover:text-foreground">
                Läs hela svaret
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <SignatureCta
        eyebrow="Hittar du inte svaret?"
        title="Fråga oss direkt."
        body="30 minuter, kostnadsfritt. Vi svarar rakt på vad film skulle kosta er och vilken modell som passar — Pulse, Prime eller en hybrid."
        tone="mint"
      />

      <TrustBand ctaLocation="fragor_hub_trust_band" />
    </div>
  );
}
